import { create } from 'zustand';

// Cache key: "<userId>:<year>"
type CacheKey = string;

interface YearCache {
  intensityMap: Map<string, number>;
  fetchedAt: number;
}

interface HeatmapState {
  // Per-(userId, year) cache
  cache: Map<CacheKey, YearCache>;

  // Loading / error keyed the same way so each year has independent status
  loadingKeys: Set<CacheKey>;
  errorKeys: Map<CacheKey, string>;

  // Actions
  setYearData: (userId: string, year: number, data: Map<string, number>) => void;
  setYearLoading: (userId: string, year: number, loading: boolean) => void;
  setYearError: (userId: string, year: number, error: string | null) => void;
  clearYearCache: (userId: string, year: number) => void;
  clearAllCache: () => void;

  // Selectors
  getIntensityMap: (userId: string, year: number) => Map<string, number>;
  isYearCacheValid: (userId: string, year: number, maxAge?: number) => boolean;
  isYearLoading: (userId: string, year: number) => boolean;
  getYearError: (userId: string, year: number) => string | null;
}

const CACHE_TTL = 3_600_000; // 1 hour

function cacheKey(userId: string, year: number): CacheKey {
  return `${userId}:${year}`;
}

export const useHeatmapStore = create<HeatmapState>((set, get) => ({
  cache: new Map(),
  loadingKeys: new Set(),
  errorKeys: new Map(),

  setYearData: (userId, year, data) => {
    const key = cacheKey(userId, year);
    set((state) => {
      const next = new Map(state.cache);
      next.set(key, { intensityMap: data, fetchedAt: Date.now() });
      const nextLoading = new Set(state.loadingKeys);
      nextLoading.delete(key);
      const nextErrors = new Map(state.errorKeys);
      nextErrors.delete(key);
      return { cache: next, loadingKeys: nextLoading, errorKeys: nextErrors };
    });
  },

  setYearLoading: (userId, year, loading) => {
    const key = cacheKey(userId, year);
    set((state) => {
      const next = new Set(state.loadingKeys);
      loading ? next.add(key) : next.delete(key);
      return { loadingKeys: next };
    });
  },

  setYearError: (userId, year, error) => {
    const key = cacheKey(userId, year);
    set((state) => {
      const nextErrors = new Map(state.errorKeys);
      if (error) nextErrors.set(key, error);
      else nextErrors.delete(key);
      const nextLoading = new Set(state.loadingKeys);
      nextLoading.delete(key);
      return { errorKeys: nextErrors, loadingKeys: nextLoading };
    });
  },

  clearYearCache: (userId, year) => {
    const key = cacheKey(userId, year);
    set((state) => {
      const next = new Map(state.cache);
      next.delete(key);
      return { cache: next };
    });
  },

  clearAllCache: () => set({ cache: new Map(), loadingKeys: new Set(), errorKeys: new Map() }),

  getIntensityMap: (userId, year) => {
    const entry = get().cache.get(cacheKey(userId, year));
    return entry?.intensityMap ?? new Map();
  },

  isYearCacheValid: (userId, year, maxAge = CACHE_TTL) => {
    const entry = get().cache.get(cacheKey(userId, year));
    if (!entry) return false;
    if (Date.now() - entry.fetchedAt > maxAge) return false;
    if (entry.intensityMap.size === 0) return false;
    return true;
  },

  isYearLoading: (userId, year) => get().loadingKeys.has(cacheKey(userId, year)),

  getYearError: (userId, year) => get().errorKeys.get(cacheKey(userId, year)) ?? null,
}));
